<template>
  <div class="container">
    <h2 class="">
      <i18n path="common.install" />
    </h2>

    <p>
      <i18n path="common.guide.useNpm" />
    </p>
    <showcode :trigger="false">
      <pre>npm i -S v-gallery</pre>
    </showcode>
    <p>
      <i18n path="common.guide.include" />
    </p>
    <showcode :trigger="false">
      <v-code lang="javascript">
        <pre>
        import Vue from 'vue'
        import vGallery from 'v-gallery'
        Vue.use(vGallery)
        </pre>
      </v-code>
    </showcode>

    <h2 class="mt-5">
      <i18n path="common.deploy" />
    </h2>

    <showcode :trigger="false">
      <v-code>
        <pre>
        &lt;template&gt;
          &lt;!-- default gallery mode --&gt;
          &lt;v-gallery :images="list"  &gt;
          &lt;/v-gallery&gt;
        &lt;/template&gt;

        &lt;script&gt;
        export default {
          data(){
            return {
              list: [
                {title:'Image1',url:'http://xxx.com/1.jpg'},
                {title:'Image2',url:'http://xxx.com/2.jpg'},
                {...}
              ]
            }
          }
        }
        &lt;/script&gt;
        </pre>
      </v-code>
    </showcode>

    <h2 class="mt-5">
      <i18n path="common.example" />
    </h2>

    <h5 class="mt-5">
      <h5 path="gallery.demo.carousel" />
    </h5>
    <p>
      <v-gallery
        type="carousel"
        :images="list"
      />
    </p>
    <showcode>
      <v-code>
        <pre>
        &lt;v-gallery type="carousel" :images="list"&gt;&lt;/v-gallery&gt;
        </pre>
      </v-code>
    </showcode>

    <h5 class="mt-5">
      <i18n path="gallery.demo.galleryLight" />
    </h5>
    <p>
      <v-gallery
        :images="list"
        :caption="true"
      />
    </p>
    <showcode>
      <v-code>
        <pre>
        &lt;v-gallery :images="list":caption="true" &gt;&lt;/v-gallery&gt;
        </pre>
      </v-code>
    </showcode>

    <h5 class="mt-5">
      <i18n path="gallery.demo.galleryDark" />
    </h5>
    <p>
      <v-gallery
        :images="list"
        :dark="true"
        :caption="true"
      />
    </p>
    <showcode>
      <v-code>
        <pre>
        &lt;v-gallery :images="list" :dark="true" :caption="true" &gt;&lt;/v-gallery&gt;
        </pre>
      </v-code>
    </showcode>

    <h5 class="mt-5">
      <i18n path="gallery.demo.galleryCustom" />
    </h5>
    <v-gallery
      :images="list"
      class="image-box"
    >
      <a
        href="javascript:void(0);"
        :data-image="img.url"
        :title="img.title"
        v-for="(img, index) in list"
        :key="index"
      >
        <div class="bgbox">
          <img :src="img.url">
        </div>
        <div
          class="img-title"
          v-html="img.title"
        />
      </a>
    </v-gallery>
    <showcode>
      <v-code>
        <pre>
        &lt;template&gt;
          &lt;v-gallery :images="list" class="image-box"&gt;
            &lt;a href="javascript:void(0);"
                :data-image="img.url"
                :title="img.title"
                v-for="img in list" &gt;
              &lt;div class="bgbox"&gt;
                &lt;img :src="img.url"&gt;
              &lt;/div&gt;
              &lt;div class="img-title" v-html="img.title"&gt;&lt;/div&gt;
            &lt;/a&gt;
          &lt;/v-gallery&gt;
        &lt;/template&gt;
        &lt;style lang="scss"&gt;
        .image-box{
          a {
            clear: both;
            display: inline-block;
            margin: 0 10px 10px 0;
            position: relative;
            text-align: center;
            .bgbox {
              background-color: #232323;
              height: 150px;
              display:table-cell;
              vertical-align:middle;
              padding: 4px;
              border-radius: 2px;
              img{ width: 200px;display: block;}
            }
            .img-title {
              bottom: 5px;
              display: block;
              text-align: center;
              color: #999999;
              padding-top: 5px;
            }
            &amp;:hover {
              .img-title { color: #232323; }
            }
          }
        }
        &lt;/style&gt;
        </pre>
      </v-code>
    </showcode>

    <h2 class="mt-5">
      <i18n path="common.options" />
    </h2>
    <table class="table table-bordered table-striped option-table">
      <thead>
        <tr>
          <th>
            <i18n path="common.tbTitle.name" />
          </th>
          <th>
            <i18n path="common.tbTitle.type" />
          </th>
          <th>
            <i18n path="common.tbTitle.desc" />
          </th>
          <th>
            <i18n path="common.tbTitle.required" />
          </th>
          <th>
            <i18n path="common.tbTitle.default" />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>type</td>
          <td>String</td>
          <td>
            <p>
              <i18n path="gallery.option.type" />
            </p>
            <ul>
              <li>
                <code>gallery</code>
              </li>
              <li>
                <code>carousel</code>
              </li>
            </ul>
          </td>
          <td>false</td>
          <td>gallery</td>
        </tr>
        <tr>
          <td>images</td>
          <td>Array</td>
          <td>
            <p>
              <i18n path="gallery.option.images" />
            </p>
            <showcode :trigger="false">
              <v-code lang="javascript">
                <pre>
                [
                  {
                    title: 'img1',
                    url:'http://image_url',
                    thumbnail:'http://image_thumbnail'
                  },
                  {...}
                ]
                </pre>
              </v-code>
            </showcode>
          </td>
          <td>true</td>
          <td />
        </tr>
        <tr>
          <td>control-pause</td>
          <td>Boolean</td>
          <td>
            <i18n path="gallery.option.pause" />
          </td>
          <td>false</td>
          <td>false</td>
        </tr>
        <tr>
          <td>control-title</td>
          <td>Boolean</td>
          <td>
            <i18n path="gallery.option.title" />
          </td>
          <td>false</td>
          <td>true</td>
        </tr>
        <tr>
          <td>dark</td>
          <td>Boolean</td>
          <td>
            <i18n path="gallery.option.dark" />
          </td>
          <td>false</td>
          <td>false</td>
        </tr>
        <tr>
          <td>caption</td>
          <td>Boolean</td>
          <td>
            <i18n path="gallery.option.caption" />
          </td>
          <td>false</td>
          <td>false</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  data () {
    return {
      list: [
        { url: require('../../assets/gallery/1.jpg'), title: 'image1' },
        { url: require('../../assets/gallery/2.jpg'), title: 'image2' },
        { url: require('../../assets/gallery/3.jpg'), title: 'image3' },
        { url: require('../../assets/gallery/4.jpg'), title: 'image4' },
        { url: require('../../assets/gallery/5.jpg'), title: 'image5' },
        { url: require('../../assets/gallery/6.jpg'), title: 'image6' },
        { url: require('../../assets/gallery/7.jpg'), title: 'image7' },
        { url: require('../../assets/gallery/8.jpg'), title: 'image8' },
        { url: require('../../assets/gallery/9.jpg'), title: 'image9' },
        { url: require('../../assets/gallery/10.jpg'), title: 'image10' }
      ]
    }
  },
  methods: {
    resultFormat (row) {
      if (row) {
        return row.name + ' (' + row.desc + ')'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.image-box {
  /*border: 1px solid #DDDDDD;*/
  /*padding: 5px;*/

  a {
    clear: both;
    display: inline-block;
    margin: 0 10px 10px 0;
    position: relative;
    text-align: center;
    .bgbox {
      background-color: #232323;
      height: 150px;
      display: table-cell;
      vertical-align: middle;
      padding: 4px;
      border-radius: 2px;
      img {
        width: 200px;
        display: block;
      }
    }
    .img-title {
      bottom: 5px;
      display: block;
      text-align: center;
      color: #999999;
      padding-top: 5px;
    }
    &:hover {
      .img-title {
        color: #232323;
      }
    }
  }
}
</style>
